import React, { useEffect, useState } from "react";
import { RefreshCw, Upload, CloudSun, CalendarClock, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { api, formatApiError } from "../../lib/api";
import GenerationComparison from "./GenerationComparison";
import { fmtKwh, fmtDateGB, ukShort } from "../../lib/format";
import ImportCsvDialog from "./ImportCsvDialog";
import SyncHistoryDialog from "./SyncHistoryDialog";
import WeatherBackfillDialog from "./WeatherBackfillDialog";
import MonthlySummary from "./MonthlySummary";
import PPAContractsSection from "./PPAContracts";
import FinancialKpis from "../../components/FinancialKpis";
import { useFeatures } from "../../context/FeatureContext";
import DateInput from "../../components/DateInput";

// Distinct hues for the multi-site compare chart. First 3 match the app's
// primary palette (ink → accent → signal); the rest are chosen to remain
// legible on white without echoing the P&L purple.
const COMPARE_COLORS = ["#F59E0B", "#00D6DF", "#7C3AED", "#0EA5E9", "#EC4899",
                        "#84CC16", "#EF4444", "#8B5CF6", "#F97316", "#14B8A6"];

export default function MonitoringTab({ projectId, project, design, monitoring, reload }) {
  const [busy, setBusy] = useState(false);
  const [showCsv, setShowCsv] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [showWeatherBackfill, setShowWeatherBackfill] = useState(false);
  const { canUse } = useFeatures();
  // Local fetch with weather forecast enabled — separate from the parent's
  // cached monitoring prop (which is fetched without the overlay to keep the
  // rest of the project detail fast).
  const [forecastData, setForecastData] = useState(null);
  const [forecastEnabled, setForecastEnabled] = useState(true);
  const [group, setGroup] = useState("daily");   // hourly | daily | monthly | yearly | ytd | custom
  const [scope, setScope] = useState("site");    // site | portfolio
  // Custom-range date pickers — only sent when group === "custom".
  // Defaults to the last 30 days so the initial fetch always returns something.
  const [customStart, setCustomStart] = useState(
    () => new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
  );
  const [customEnd, setCustomEnd] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  // Series visibility toggles — user can enable/disable any of the six
  // chart series individually to declutter or to focus on variance signals.
  const [seriesOn, setSeriesOn] = useState({
    actual: true, generation: true, import: true, export: true,
    simulated: true, weather: true,
  });
  const toggleSeries = (k) =>
    setSeriesOn((prev) => ({ ...prev, [k]: !prev[k] }));
  // Multi-site overlay
  const [allSites, setAllSites] = useState([]);              // list of {id,name}
  const [compareIds, setCompareIds] = useState([]);          // selected sites
  const [compareData, setCompareData] = useState(null);      // wide-format frame
  // Per-meter filter — when set, monitoring reads only 15-min rows tagged
  // with this meter_id. Populated from the project's registered meters.
  const [meters, setMeters] = useState([]);
  const [meterFilter, setMeterFilter] = useState("");        // "" = all

  useEffect(() => {
    // Load project list once for the compare picker
    api.get("/projects").then((r) => setAllSites(
      (r.data || []).map((p) => ({ id: p.id, name: p.name })),
    )).catch(() => {});
    // Load this project's meters for the per-meter filter dropdown.
    api.get(`/projects/${projectId}/meters`).then((r) =>
      setMeters((r.data || []).filter((m) => m.active)),
    ).catch(() => {});
  }, [projectId]);

  const toggleCompareSite = (sid) => {
    setCompareIds((prev) => prev.includes(sid)
      ? prev.filter((x) => x !== sid)
      : [...prev, sid]);
  };

  const loadCompare = async () => {
    if (compareIds.length === 0) { setCompareData(null); return; }
    try {
      const sites = [projectId, ...compareIds].filter((v, i, a) => a.indexOf(v) === i).join(",");
      const { data } = await api.get(
        `/monitoring/compare?sites=${sites}&metric=actual_kwh&group=${group === "hourly" ? "daily" : group}`,
      );
      setCompareData(data);
    } catch { setCompareData(null); }
  };
  useEffect(() => { loadCompare(); }, [projectId, group, compareIds.join(",")]);

  const loadForecast = async () => {
    try {
      const custom = group === "custom"
        ? `&start_date=${customStart}&end_date=${customEnd}`
        : "";
      const meterQ = meterFilter && scope !== "portfolio" ? `&meter_id=${meterFilter}` : "";
      const url = scope === "portfolio"
        ? `/monitoring/portfolio?forecast=1&days_forward=7&group=${group}${custom}`
        : `/monitoring/by-project/${projectId}?forecast=1&days_forward=7&group=${group}${custom}${meterQ}`;
      const { data } = await api.get(url);
      setForecastData(data);
    } catch { /* silent — chart falls back to non-forecast rendering */ }
  };
  useEffect(() => { loadForecast(); },
    [projectId, group, scope, customStart, customEnd, meterFilter]);

  const sync = async () => {
    if (!design) { toast.error("Add a design first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/monitoring/sync/${projectId}`);
      toast.success(`Synced ${data.count} days from ${data.source}`);
      reload();
      loadForecast();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Download the current chart data as CSV / PDF. We fetch as blob via the
  // existing axios instance so httpOnly cookie auth flows correctly (browser
  // window.open would drop the SameSite cookie and 401 the request).
  const downloadExport = async (fmt) => {
    try {
      const custom = group === "custom"
        ? `&start_date=${customStart}&end_date=${customEnd}`
        : "";
      const url = `/monitoring/${projectId}/export.${fmt}` +
                  `?group=${group}&forecast=${forecastEnabled ? 1 : 0}${custom}`;
      const res = await api.get(url, { responseType: "blob" });
      const filename = (res.headers["content-disposition"] || "")
        .match(/filename="?([^"]+)"?/)?.[1] || `monitoring.${fmt}`;
      const blob = new Blob([res.data], {
        type: fmt === "pdf" ? "application/pdf" : "text/csv",
      });
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      setTimeout(() => {
        URL.revokeObjectURL(dl.href);
        dl.remove();
      }, 200);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `${fmt.toUpperCase()} export failed`);
    }
  };

  // Use forecast-enriched samples when available; fall back to the parent's
  // plain monitoring prop otherwise.
  const chartSamples = (forecastEnabled && forecastData?.samples) || monitoring?.samples || [];
  const samples = monitoring?.samples || [];
  const perf = monitoring?.performance_ratio_pct || 0;
  const fcMeta = forecastData?.forecast;
  const todayIso = new Date().toISOString().slice(0, 10);
  const isDaily = group === "daily";

  return (
    <div className="space-y-6" data-testid="monitoring-tab">
      <GenerationComparison projectId={projectId} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-1 bg-[var(--surface-2)]">
        <div className="bg-white p-5 border border-[var(--line)]">
          <div className="label">Actual (90d)</div>
          <div className="kpi-value mt-1">{fmtKwh(monitoring?.total_actual_kwh || 0)}</div>
        </div>
        <div className="bg-white p-5 border border-[var(--line)]">
          <div className="label">Simulated (90d)</div>
          <div className="kpi-value mt-1">{fmtKwh(monitoring?.total_simulated_kwh || 0)}</div>
        </div>
        <div className="bg-white p-5 border border-[var(--line)] bg-[var(--ink)] text-white">
          <div className="label !text-white/70">Performance</div>
          <div className="kpi-value mt-1 text-white">{perf.toFixed(1)}%</div>
          <div className="text-xs text-white/70">{perf >= 95 ? "On target" : perf >= 80 ? "Slightly underperforming" : "Investigate"}</div>
        </div>
        <div className="bg-white p-5 border border-[var(--line)] flex flex-col justify-between">
          <div>
            <div className="label">Data source</div>
            <div className="font-mono text-xs mt-1">{samples[0]?.source || "—"}</div>
            <div className="text-xs muted">
              Source: {project.data_source
                ? (project.data_source === "solaredge" && project.solaredge_site_id
                    ? `SolarEdge · ${project.solaredge_site_id}`
                    : project.data_source.charAt(0).toUpperCase() + project.data_source.slice(1))
                : (project.solaredge_site_id ? `SolarEdge · ${project.solaredge_site_id}` : "—")}
            </div>
          </div>
          <button onClick={sync} disabled={busy} className="btn-primary mt-3 justify-center" data-testid="sync-monitoring-btn">
            <RefreshCw size={13} /> {busy ? "Syncing…" : "Sync 90 days"}
          </button>
          <button onClick={() => setShowSyncHistory(true)} className="btn-ghost mt-2 justify-center"
                  data-testid="sync-history-btn">
            <CalendarClock size={13} /> Sync history…
          </button>
          <button onClick={() => setShowWeatherBackfill(true)} className="btn-ghost mt-2 justify-center"
                  data-testid="weather-backfill-btn"
                  title="Backfill historic Open-Meteo weather (GHI) so historic variance charts can be reconstructed">
            <CloudSun size={13} /> Backfill weather…
          </button>
          <button onClick={() => setShowCsv(true)} className="btn-primary mt-2 justify-center bg-[var(--accent)] border-[var(--accent)]"
                  disabled={!canUse("csv_import")}
                  title={canUse("csv_import") ? "" : "Disabled by feature toggles"}
                  data-testid="import-csv-btn">
            <Upload size={13} /> Import CSV / Excel
          </button>
        </div>
      </div>

      <FinancialKpis projectId={projectId} />

      <section className="swiss-card-flat p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="label">
            {scope === "portfolio" ? "Portfolio" : "Site"} · Actual vs simulated vs weather forecast · {group === "ytd" ? "year-to-date" : group}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex bg-[var(--surface-2)] border border-[var(--line)]" data-testid="scope-picker">
              {[
                { k: "site", label: "This site" },
                { k: "portfolio", label: "All sites" },
              ].map(o => (
                <button key={o.k} onClick={() => setScope(o.k)}
                        data-testid={`scope-${o.k}`}
                        className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${scope === o.k ? "bg-[var(--ink)] text-white" : "muted"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap bg-[var(--surface-2)] border border-[var(--line)] max-w-full" data-testid="group-picker">
              {[
                { k: "hourly",  label: "Hourly"  },
                { k: "daily",   label: "Daily"   },
                { k: "monthly", label: "Monthly" },
                { k: "yearly",  label: "Yearly"  },
                { k: "ytd",     label: "YTD"     },
                { k: "custom",  label: "Custom"  },
              ].map(o => (
                <button key={o.k} onClick={() => setGroup(o.k)}
                        data-testid={`group-${o.k}`}
                        className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${group === o.k ? "bg-[var(--accent)] text-white" : "muted"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {group === "custom" && (
              <div className="flex items-center gap-1 border border-[var(--line)] bg-white px-2 py-1"
                   data-testid="custom-range-pickers">
                <DateInput value={customStart}
                       onChange={(e) => setCustomStart(e.target.value)}
                       max={customEnd}
                       className="text-[11px] font-mono border-0 focus:outline-none"
                       data-testid="custom-start-date" />
                <span className="muted text-[10px]">→</span>
                <DateInput value={customEnd}
                       onChange={(e) => setCustomEnd(e.target.value)}
                       min={customStart}
                       max={new Date().toISOString().slice(0, 10)}
                       className="text-[11px] font-mono border-0 focus:outline-none"
                       data-testid="custom-end-date" />
              </div>
            )}
            {/* Per-meter filter — only shown for project scope (portfolio
                view aggregates across all sites/meters). "All meters" is
                the default; picking a specific one filters the chart to
                only rows tagged with that meter's `meter_id`. */}
            {scope !== "portfolio" && meters.length > 0 && (
              <select value={meterFilter}
                      onChange={(e) => setMeterFilter(e.target.value)}
                      className="input-swiss text-[11px] font-mono px-2 py-1"
                      data-testid="meter-filter-select"
                      title="Filter the chart to one specific meter">
                <option value="">All meters</option>
                {meters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {m.install_group ? ` · ${m.install_group}` : ""}
                    {` (${m.meter_type})`}
                  </option>
                ))}
              </select>
            )}
            {fcMeta?.enabled ? (
              <span className="text-[10px] font-mono uppercase tracking-widest muted flex items-center gap-1.5"
                    data-testid="forecast-meta">
                <CloudSun size={11} className="text-[var(--accent)]" />
                {scope === "portfolio"
                  ? `Weather · ${fcMeta.sites_with_forecast}/${fcMeta.sites_total} sites`
                  : `Weather · ${fcMeta.system_kwp} kWp × PR ${fcMeta.performance_ratio}`}
              </span>
            ) : forecastData ? (
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700" data-testid="forecast-warn">
                Weather off · {fcMeta?.reason === "missing_lat_lng" ? "add lat/lng to enable"
                  : fcMeta?.reason === "no_design" ? "no design saved"
                  : fcMeta?.reason === "no_system_size" ? "systemSize is 0"
                  : "Open-Meteo unavailable"}
              </span>
            ) : null}
            <label className="text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={forecastEnabled}
                     onChange={(e) => setForecastEnabled(e.target.checked)}
                     data-testid="forecast-toggle" />
              Forecast overlay
            </label>
            <button onClick={() => downloadExport("csv")}
                    disabled={!chartSamples.length}
                    className="btn-ghost text-[10px] disabled:opacity-40"
                    data-testid="export-csv-btn"
                    title="Download the current chart data as CSV (opens in Excel)">
              <Download size={11} /> CSV
            </button>
            <button onClick={() => downloadExport("pdf")}
                    disabled={!chartSamples.length}
                    className="btn-ghost text-[10px] disabled:opacity-40"
                    data-testid="export-pdf-btn"
                    title="Landlord/investor-ready PDF report with KPIs + full data table">
              <FileText size={11} /> PDF
            </button>
          </div>
        </div>

        {/* Series toggles — turn any of the six chart series on/off */}
        <div className="flex flex-wrap gap-1 mb-3 pb-3 border-b border-[var(--line)]"
             data-testid="series-toggles">
          {[
            { k: "actual",     label: "Actual",     color: "#F59E0B" },
            { k: "generation", label: "Generation", color: "#10B981" },
            { k: "import",     label: "Import",     color: "#EF4444" },
            { k: "export",     label: "Export",     color: "#3B82F6" },
            { k: "simulated",  label: "Simulated",  color: "#52525B" },
            { k: "weather",    label: "Weather",    color: "#00D6DF" },
          ].map((s) => (
            <button key={s.k} onClick={() => toggleSeries(s.k)}
                    data-testid={`series-toggle-${s.k}`}
                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors flex items-center gap-1.5 ${
                      seriesOn[s.k]
                        ? "bg-[var(--surface-2)] border-[var(--line)] text-[var(--ink)]"
                        : "bg-white border-[var(--line)] text-zinc-400 line-through"}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: seriesOn[s.k] ? s.color : "#D4D4D8" }} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Compare-with-other-sites picker (only in per-site scope) */}
        {scope === "site" && allSites.length > 1 && (
          <div className="mb-4 p-3 bg-[var(--surface-2)] border border-[var(--line)]"
               data-testid="compare-sites-picker">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="label">Compare with</div>
              {compareIds.length > 0 && (
                <button onClick={() => setCompareIds([])}
                        className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                        data-testid="compare-clear-btn">
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {allSites.filter((s) => s.id !== projectId).slice(0, 12).map((s) => {
                const active = compareIds.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggleCompareSite(s.id)}
                          data-testid={`compare-site-${s.id}`}
                          className={`px-2.5 py-1 text-[10px] font-mono border transition-colors ${
                            active
                              ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                              : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--accent)]"}`}>
                    {s.name}
                  </button>
                );
              })}
            </div>
            {compareData && (
              <div className="mt-2 text-[11px] font-mono muted">
                {compareData.sites.map((s) => `${s.name}: ${s.total.toLocaleString()} kWh`).join(" · ")}
              </div>
            )}
          </div>
        )}
        {chartSamples.length === 0 ? (
          <div className="muted text-sm">No monitoring data yet. Click <strong>Sync 90 days</strong> to pull from SolarEdge API (or generate a synthetic 90-day window using the design simulation when no API key is configured).</div>
        ) : (
          <div style={{ width: "100%", height: 340 }} data-testid="monitoring-chart">
            <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1}>
              <ComposedChart data={chartSamples} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                <defs>
                  <linearGradient id="simG2" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#A1A1AA" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#A1A1AA" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="actG2" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" tickFormatter={ukShort} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}MWh` : `${v}kWh`} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => `${v}%`} domain={[-100, 100]} />
                <Tooltip labelFormatter={fmtDateGB} contentStyle={{ borderRadius: 0, fontSize: 12 }}
                         formatter={(v, name) => v == null ? ["—", name]
                           : name === "Variance %" ? [`${Number(v).toFixed(1)}%`, name]
                           : [`${Number(v).toLocaleString()} kWh`, name]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {isDaily && forecastEnabled && fcMeta?.enabled && (
                  <ReferenceLine yAxisId="l" x={todayIso} stroke="#00D6DF" strokeDasharray="3 3"
                                 label={{ value: "today", position: "top", fill: "#00D6DF",
                                          fontSize: 9, fontFamily: "IBM Plex Mono" }} />
                )}
                {seriesOn.simulated && (
                  <Area yAxisId="l" dataKey="simulated_kwh" name="Design simulated" stroke="#52525B"
                        fill="url(#simG2)" strokeWidth={1} />
                )}
                {seriesOn.actual && (
                  <Area yAxisId="l" dataKey="actual_kwh" name="Actual" stroke="#F59E0B"
                        fill="url(#actG2)" strokeWidth={2} connectNulls={false} />
                )}
                {seriesOn.generation && (
                  <Line yAxisId="l" dataKey="generation_kwh" name="Generation (MeterOnline)"
                        stroke="#10B981" strokeWidth={2} dot={false} connectNulls />
                )}
                {seriesOn.import && (
                  <Line yAxisId="l" dataKey="import_kwh" name="Import"
                        stroke="#EF4444" strokeWidth={1.5} dot={false} connectNulls />
                )}
                {seriesOn.export && (
                  <Line yAxisId="l" dataKey="export_kwh" name="Export"
                        stroke="#3B82F6" strokeWidth={1.5} dot={false} connectNulls />
                )}
                {seriesOn.weather && forecastEnabled && fcMeta?.enabled && (
                  <Line yAxisId="l" dataKey="weather_estimate_kwh" name="Weather forecast"
                        stroke="#00D6DF" strokeWidth={2} strokeDasharray="5 3"
                        dot={false} connectNulls />
                )}
                <Line yAxisId="r" dataKey="variance_pct" name="Variance %"
                      stroke="#7C3AED" strokeWidth={1.5} dot={false} connectNulls />
                <ReferenceLine yAxisId="r" y={0} stroke="#A1A1AA" strokeDasharray="2 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Variance KPI cards — Actual vs Simulated, Actual vs Weather */}
        {chartSamples.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-1 mt-4 bg-[var(--surface-2)]"
               data-testid="monitoring-kpi-cards">
            {(() => {
              const sum = (k) => chartSamples.reduce((a, r) => a + (Number(r[k]) || 0), 0);
              const actual = sum("actual_kwh") || sum("generation_kwh");
              const simulated = sum("simulated_kwh");
              const weather = sum("weather_estimate_kwh");
              const imp = sum("import_kwh");
              const exp = sum("export_kwh");
              // REGO count — 1 MWh of generation = 1 REGO. Metered generation
              // preferred; falls back to actual for non-MO sites where
              // actual_kwh IS the generation series.
              const genForRego = sum("generation_kwh") || actual;
              const regos = Math.floor(genForRego / 1000);
              const vsSim = simulated > 0 ? ((actual - simulated) / simulated) * 100 : null;
              const vsWx  = weather   > 0 ? ((actual - weather)   / weather)   * 100 : null;
              const fmtVar = (v) => v == null ? "—" :
                `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
              return (
                <>
                  <MonitoringKpi label="vs Simulated" value={fmtVar(vsSim)}
                                 tone={vsSim == null ? "muted" : vsSim >= -5 ? "ok" : vsSim >= -15 ? "warn" : "danger"}
                                 testid="kpi-var-sim" />
                  <MonitoringKpi label="vs Weather" value={fmtVar(vsWx)}
                                 tone={vsWx == null ? "muted" : Math.abs(vsWx) <= 10 ? "ok" : Math.abs(vsWx) <= 25 ? "warn" : "danger"}
                                 testid="kpi-var-wx" />
                  <MonitoringKpi label="Import (period)"
                                 value={imp > 0 ? fmtKwh(imp) : "—"}
                                 tone="muted" testid="kpi-import-total" />
                  <MonitoringKpi label="Export (period)"
                                 value={exp > 0 ? fmtKwh(exp) : "—"}
                                 tone="muted" testid="kpi-export-total" />
                  <MonitoringKpi label="REGOs · 1 MWh = 1"
                                 value={regos.toLocaleString()}
                                 tone={regos > 0 ? "ok" : "muted"}
                                 testid="kpi-regos" />
                </>
              );
            })()}
          </div>
        )}
      </section>

      {/* Multi-site compare chart — renders below the main chart when the
          user has picked at least one comparison site. Kept as a separate
          section so it can be hidden without disturbing the primary chart. */}
      {compareData && compareData.sites.length >= 2 && (
        <section className="swiss-card-flat p-6" data-testid="compare-chart-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="label">Side-by-side comparison</div>
              <div className="text-xs muted mt-1">
                Actual production · {compareData.group} · {compareData.sites.length} sites
              </div>
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }} data-testid="compare-chart">
            <ResponsiveContainer width="99%" height="100%">
              <ComposedChart data={compareData.samples} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" tickFormatter={ukShort} />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                       tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}MWh` : `${v}kWh`} />
                <Tooltip labelFormatter={fmtDateGB} contentStyle={{ borderRadius: 0, fontSize: 12 }}
                         formatter={(v) => v == null ? "—" : `${Number(v).toLocaleString()} kWh`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {compareData.sites.map((s, i) => (
                  <Line key={s.project_id} dataKey={s.project_id} name={s.name}
                        stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                        strokeWidth={i === 0 ? 2.5 : 1.5} dot={false} connectNulls />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* PPA contract management — per-site agreements + expiry warnings */}
      {canUse("ppa_contracts") && <PPAContractsSection projectId={projectId} />}

      {/* Monthly summary dashboard — REGO-aware calendar-year view */}
      {scope === "site" && <MonthlySummary projectId={projectId} />}

      {showCsv && (
        <ImportCsvDialog
          projectId={projectId}
          onClose={() => setShowCsv(false)}
          onImported={() => { setShowCsv(false); reload && reload(); }}
        />
      )}

      {showSyncHistory && (
        <SyncHistoryDialog
          projectId={projectId}
          onClose={() => setShowSyncHistory(false)}
          onDone={() => { reload && reload(); }}
        />
      )}

      {showWeatherBackfill && (
        <WeatherBackfillDialog
          projectId={projectId}
          onClose={() => setShowWeatherBackfill(false)}
          onDone={() => { reload && reload(); loadForecast(); }}
        />
      )}
    </div>
  );
}



function MonitoringKpi({ label, value, tone = "muted", testid }) {
  const tones = {
    ok:     "bg-emerald-50 text-emerald-900 border-emerald-200",
    warn:   "bg-amber-50 text-amber-900 border-amber-200",
    danger: "bg-red-50 text-red-800 border-red-200",
    muted:  "bg-white text-[var(--ink)] border-[var(--line)]",
  };
  return (
    <div className={`p-4 border ${tones[tone] || tones.muted}`} data-testid={testid}>
      <div className="label">{label}</div>
      <div className="font-display text-xl font-bold mt-1 font-mono">{value}</div>
    </div>
  );
}
